import React from 'react'
import Select from 'react-select'
import type { GroupBase, StylesConfig } from 'react-select'

type Option = { label: string; value: string }

function mergeSelectStyles(
  baseStyles: StylesConfig<Option, true, GroupBase<Option>>,
  overrideStyles?: StylesConfig<Option, true, GroupBase<Option>>
): StylesConfig<Option, true, GroupBase<Option>> {
  if (!overrideStyles) return baseStyles
  const keys = Array.from(
    new Set([
      ...Object.keys(baseStyles),
      ...Object.keys(overrideStyles),
    ])
  )
  const merged: Record<string, (base: unknown, props: unknown) => unknown> = {
    ...(baseStyles as Record<string, (base: unknown, props: unknown) => unknown>),
  }
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const baseFn = merged[key]
    const overrideFn = overrideStyles[key as keyof typeof overrideStyles] as
      | ((base: unknown, props: unknown) => unknown)
      | undefined
    if (typeof baseFn === 'function' && typeof overrideFn === 'function') {
      merged[key] = (base, props) => overrideFn(baseFn(base, props), props)
    } else if (typeof overrideFn === 'function') {
      merged[key] = overrideFn
    }
  }
  return merged as StylesConfig<Option, true, GroupBase<Option>>
}

const defaultStyles: StylesConfig<Option, true, GroupBase<Option>> = {
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
}

export const MultiSelect = ({ 
  options, 
  value, 
  onChange, 
  placeholder,
  styles,
}: { 
  options: { label: string; value: string; }[], 
  value: string[], 
  onChange: (value: string[]) => void, 
  placeholder: string,
  styles?: StylesConfig<Option, true, GroupBase<Option>>
}) => {
  // Convert value array to array of objects for Select
  const selectedValues = value.map(val => ({
    label: options.find(opt => opt.value === val)?.label || val,
    value: val
  }));

  // Handle Select onChange to return just the values array
  const handleChange = (selectedOptions: any) => {
    const selectedValues = selectedOptions ? 
      selectedOptions.map((option: any) => option.value) : 
      [];
    onChange(selectedValues);
  };

  const mergedStyles = mergeSelectStyles(defaultStyles, styles)

  return (
    <div 
      className="w-full" 
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Select
        options={options}
        value={selectedValues}
        onChange={handleChange}
        isMulti
        placeholder={placeholder}
        components={{ IndicatorSeparator: () => null }}
        isDisabled={options.length === 0}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={mergedStyles}
      />
    </div>
  );
};

// Usage example:
/*
const options = [
  { label: "Option 1", value: "opt1" },
  { label: "Option 2", value: "opt2" },
];
const values = ["opt1", "opt2"];  // Array of just values

<MultiSelect
  options={options}
  value={values}
  onChange={(newValues) => {
    // newValues will be an array of strings like ["opt1", "opt2"]
    setValues(newValues);
  }}
  placeholder="Select options"
/>
*/
